/**
 * Commitment state — announced vs committed. Pure judgment library.
 *
 * PRD: docs/9-exploration/DISPLACEMENT-AND-COMMITMENT-STATES-PRD.md (D1).
 * D0 record (what the data actually looks like):
 *   docs/9-exploration/DISPLACEMENT-D0-VERIFICATION-RECORD.md.
 *
 * The distinction this module exists to draw, in Wojnowski's words: if
 * leadership doesn't name and accept the tradeoff, "the decision isn't made.
 * It's just announced." A priority reaches `ratified` today without one hour
 * or one dollar being bound to it. This computes the missing overlay.
 *
 * THE STATE IS DERIVED, NEVER STORED. Same discipline as "overdue is
 * computed, never stored" (commitmentTerms.ts): priority_sets already owns
 * proposed → decided → ratified → published, and this is not a fifth state on
 * that ladder — it is a read of whether resources followed the decision.
 *
 * Pure on purpose (commitmentTerms.ts / cutReasons.ts precedent): no React,
 * no Supabase, no stores. Callers supply requirements and claims; this decides.
 *
 * Binding language rule (L1 + plain-language rule): every user-facing string
 * says what happened in plain words, and no invented specificity — when the
 * data cannot support a judgment, the state says so rather than guessing.
 */

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/**
 * One line of recipe-derived demand for a commitment: hours of a named
 * resource type. Produced from `computeRecipeResourceDemand()` in
 * src/types/work-type-recipes.ts — this module never does capacity math of
 * its own (PRD §1: "this PRD adds no new capacity math").
 */
export interface ResourceRequirementLine {
  /** Keys on the resource-type taxonomy, matching planning_capacity.resource_type. */
  resourceType: string;
  hours: number;
}

/**
 * A `plan_resource_claims` row, narrowed to what the judgment needs. Only
 * `declared` claims are live; `superseded` and `withdrawn` are history that
 * stays on the record but binds nothing (mig 681, L3).
 */
export interface CommitmentClaim {
  claimKind: 'capacity_hours' | 'funding';
  status: 'declared' | 'superseded' | 'withdrawn';
  /** Null on hand-made claims — see `unattributedHours` below. */
  resourceType?: string | null;
  declaredHours?: number | null;
  declaredAmount?: number | null;
  currency?: string | null;
}

export interface DeriveCommitmentStateInput {
  /**
   * Recipe-derived requirement lines. An EMPTY array means "no requirement
   * basis" — either the item has no work type, its work type has no recipe,
   * or the recipe has no demand template (all three are the normal case in
   * production today: D0-2 / D0-3). It does NOT mean "requires nothing".
   */
  requirements: readonly ResourceRequirementLine[];
  /** Every claim on this commitment, live or not — filtering happens here. */
  claims: readonly CommitmentClaim[];
  /**
   * Fraction of the requirement that must be covered to read `committed`.
   * Default 1 (100% of the firm-band requirement — PRD §4). Configurable
   * because Patel's orgs may hold a different line; PRD open question 1.
   */
  threshold?: number;
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

/**
 * `unknown` is not a third rung on the ladder — it is this module's
 * `basis_state='no_basis'` (mig 681's own discipline): resources ARE claimed
 * but there is no requirement to measure them against, so no honest verdict
 * exists. Never render it as either committed or announced.
 */
export type CommitmentState = 'committed' | 'announced' | 'unknown';

export type CommitmentStateBasis =
  /** No live claim of any kind — nothing is bound. Needs no requirement. */
  | 'no_claims'
  /** Live claims exist, but nothing says what this commitment needs. */
  | 'no_requirement'
  /** Requirement and claims both present; coverage was computed. */
  | 'measured';

export interface CommitmentStateView {
  state: CommitmentState;
  basis: CommitmentStateBasis;
  /**
   * The recipe-derived requirement this verdict was measured against, echoed
   * so pool-level surfaces (D3) can aggregate demand without re-deriving it.
   * Empty when there is no requirement basis.
   */
  requirements: readonly ResourceRequirementLine[];
  /** 0–1, capped at 1. Meaningful only when basis === 'measured'. */
  coverage: number;
  /** Total recipe-derived hours required across all resource types. */
  requiredHours: number;
  /** Required hours actually covered by live capacity claims. */
  coveredHours: number;
  /** Live capacity-claim hours carrying no resource type (see below). */
  unattributedHours: number;
  /**
   * Per-resource-type shortfall, largest first — the pools a displacement
   * conversation would have to be about. Empty when fully covered.
   */
  shortfallByResourceType: readonly ResourceRequirementLine[];
  /** Count of live claims, by kind. Funding claims never affect coverage. */
  liveCapacityClaims: number;
  liveFundingClaims: number;
  /**
   * True when some covered hours came from claims with no resource type, so
   * the coverage figure is a total-hours match rather than a pool-by-pool
   * one. Surfaces must say so — that is the difference between "engineering
   * is covered" and "80 hours of something are covered".
   */
  attributionIncomplete: boolean;
  /** One plain sentence. Never says more than the data supports. */
  sentence: string;
}

/**
 * Short label for chips. The settled word for this readout is Backing:
 * announced / backed / no basis (owner, 2026-10-28). The internal key stays
 * `committed` — it is derived, never stored — and only the word people see
 * changed, because "commit" is an act word (Claim, Authorize) and not a state.
 */
export const COMMITMENT_STATE_LABELS: Record<CommitmentState, string> = {
  committed: 'Backed',
  announced: 'Announced',
  unknown: 'No basis',
};

/** Default coverage threshold — 100% of the firm-band requirement (PRD §4). */
export const DEFAULT_COMMITMENT_THRESHOLD = 1;

// ---------------------------------------------------------------------------
// The judgment
// ---------------------------------------------------------------------------

/**
 * Derives whether a commitment is backed (`committed`) or merely decided
 * (`announced`).
 *
 * Order of judgment, and why:
 *
 *  1. **No live claim → `announced`, always.** Nothing is bound, and that is
 *     knowable without any requirement at all. This is the reading production
 *     gives today for every one of its six ratified priority lists, and it is
 *     correct, not a gap in the measurement.
 *  2. **Live claims but no requirement → `unknown`.** Hours are bound; there
 *     is nothing to measure them against. Saying `committed` here would be
 *     coverage against zero — a smoothed guess dressed as a verdict.
 *  3. **Otherwise measure.** Coverage is matched per resource type: 100 hours
 *     of engineering do not cover a design requirement. Capacity claims with
 *     no resource type are applied afterwards to whatever is still short,
 *     largest shortfall first, and the result flags that it did so.
 *
 * Funding claims are counted and reported but never move coverage: the
 * requirement side of this comparison is recipe hours, and money against
 * hours is not a ratio anyone should trust. Money follow-through already has
 * its own read model (resource_claims_envelope_rollup).
 */
export function deriveCommitmentState(
  input: DeriveCommitmentStateInput
): CommitmentStateView {
  const threshold = input.threshold ?? DEFAULT_COMMITMENT_THRESHOLD;
  const live = input.claims.filter((c) => c.status === 'declared');
  const capacity = live.filter((c) => c.claimKind === 'capacity_hours');
  const funding = live.filter((c) => c.claimKind === 'funding');

  const requiredByType = new Map<string, number>();
  for (const r of input.requirements) {
    if (!r.resourceType || !(r.hours > 0)) continue;
    requiredByType.set(r.resourceType, (requiredByType.get(r.resourceType) ?? 0) + r.hours);
  }
  const requiredHours = sum(requiredByType.values());

  const base = {
    requirements: toLines(requiredByType),
    requiredHours,
    liveCapacityClaims: capacity.length,
    liveFundingClaims: funding.length,
  };

  // (1) Nothing bound.
  if (live.length === 0) {
    return {
      ...base,
      state: 'announced',
      basis: 'no_claims',
      coverage: 0,
      coveredHours: 0,
      unattributedHours: 0,
      shortfallByResourceType: toLines(requiredByType),
      attributionIncomplete: false,
      sentence:
        requiredHours > 0
          ? `Decided, but no resources are bound to it — ${formatHours(requiredHours)} required, none claimed.`
          : 'Decided, but no resources are bound to it.',
    };
  }

  // (2) Bound, but nothing to measure against.
  if (requiredHours <= 0) {
    return {
      ...base,
      state: 'unknown',
      basis: 'no_requirement',
      coverage: 0,
      coveredHours: 0,
      unattributedHours: sumHours(capacity),
      shortfallByResourceType: [],
      attributionIncomplete: false,
      sentence: describeNoRequirement(capacity.length, funding.length),
    };
  }

  // (3) Measure — attributed claims first, per resource type.
  const remaining = new Map(requiredByType);
  let coveredHours = 0;
  let unattributed = 0;

  for (const claim of capacity) {
    const hours = claim.declaredHours ?? 0;
    if (!(hours > 0)) continue;
    if (!claim.resourceType) {
      unattributed += hours;
      continue;
    }
    const need = remaining.get(claim.resourceType);
    if (need === undefined) continue; // claimed a pool this commitment does not need
    const applied = Math.min(need, hours);
    remaining.set(claim.resourceType, need - applied);
    coveredHours += applied;
  }

  // Unattributed hours fill whatever is still short, biggest gap first. This
  // is a deliberate, disclosed concession: the total is real, the pool
  // assignment is not, and `attributionIncomplete` says exactly that.
  let unattributedApplied = 0;
  if (unattributed > 0) {
    for (const [type, need] of [...remaining.entries()].sort((a, b) => b[1] - a[1])) {
      if (unattributed <= 0) break;
      if (!(need > 0)) continue;
      const applied = Math.min(need, unattributed);
      remaining.set(type, need - applied);
      coveredHours += applied;
      unattributed -= applied;
      unattributedApplied += applied;
    }
  }

  const coverage = Math.min(1, coveredHours / requiredHours);
  const state: CommitmentState = coverage >= threshold ? 'committed' : 'announced';
  const shortfall = toLines(remaining);

  return {
    ...base,
    state,
    basis: 'measured',
    coverage,
    coveredHours,
    unattributedHours: unattributedApplied,
    shortfallByResourceType: shortfall,
    attributionIncomplete: unattributedApplied > 0,
    sentence: describeMeasured(state, coverage, coveredHours, requiredHours, shortfall, unattributedApplied),
  };
}

// ---------------------------------------------------------------------------
// Portfolio rollup — the number a pilot moves
// ---------------------------------------------------------------------------

export interface CommitmentStateRollup {
  total: number;
  committed: number;
  announced: number;
  unknown: number;
  /** committed / total, 0–1. 0 when there is nothing to count. */
  committedShare: number;
  /** One plain sentence for the header line. */
  sentence: string;
}

/**
 * Rolls a set of derived states into the portfolio counter — PRD §4's
 * "12 ratified · 7 backed · 5 announced-only".
 *
 * The zero reading is deliberately explained rather than left bare: production
 * reads 0% committed on the day this ships (D0), and "the first person to see
 * it will read a working feature as a broken one" unless the surface says why.
 */
export function rollUpCommitmentStates(
  views: readonly Pick<CommitmentStateView, 'state'>[]
): CommitmentStateRollup {
  const total = views.length;
  const committed = views.filter((v) => v.state === 'committed').length;
  const announced = views.filter((v) => v.state === 'announced').length;
  const unknown = views.filter((v) => v.state === 'unknown').length;
  const committedShare = total > 0 ? committed / total : 0;

  return {
    total,
    committed,
    announced,
    unknown,
    committedShare,
    sentence: describeRollup(total, committed, announced, unknown),
  };
}

// ---------------------------------------------------------------------------
// Sentences
// ---------------------------------------------------------------------------

function describeNoRequirement(capacityClaims: number, fundingClaims: number): string {
  const bound: string[] = [];
  if (capacityClaims > 0) bound.push(`${capacityClaims} capacity claim${capacityClaims === 1 ? '' : 's'}`);
  if (fundingClaims > 0) bound.push(`${fundingClaims} funding claim${fundingClaims === 1 ? '' : 's'}`);
  return `${bound.join(' and ')} on record, but this work has no resource requirement to measure them against — set a work type with a demand template to compare.`;
}

function describeMeasured(
  state: CommitmentState,
  coverage: number,
  coveredHours: number,
  requiredHours: number,
  shortfall: readonly ResourceRequirementLine[],
  unattributedApplied: number
): string {
  const pct = Math.round(coverage * 100);
  const caveat =
    unattributedApplied > 0
      ? ` ${formatHours(unattributedApplied)} of that is claimed without a named resource type.`
      : '';

  if (state === 'committed') {
    return `Backed — ${formatHours(coveredHours)} of ${formatHours(requiredHours)} required are claimed (${pct}%).${caveat}`;
  }
  const gap = shortfall
    .slice(0, 2)
    .map((s) => `${formatHours(s.hours)} ${s.resourceType}`)
    .join(', ');
  const more = shortfall.length > 2 ? ` and ${shortfall.length - 2} more` : '';
  return gap
    ? `Short of what it needs — ${pct}% claimed; still unclaimed: ${gap}${more}.${caveat}`
    : `Short of what it needs — ${pct}% of ${formatHours(requiredHours)} claimed.${caveat}`;
}

function describeRollup(
  total: number,
  committed: number,
  announced: number,
  unknown: number
): string {
  if (total === 0) return 'Nothing ratified yet — no commitments to resource.';
  const parts = [`${total} ratified`, `${committed} backed`, `${announced} announced only`];
  if (unknown > 0) parts.push(`${unknown} with no resource requirement on file`);
  const head = parts.join(' · ');
  if (committed === 0 && announced > 0) {
    return `${head}. Nothing on this record has resources bound to it yet — that is the reading, not a missing feature.`;
  }
  return `${head}.`;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function sum(values: Iterable<number>): number {
  let t = 0;
  for (const v of values) t += v;
  return t;
}

function sumHours(claims: readonly CommitmentClaim[]): number {
  return claims.reduce((t, c) => t + (c.declaredHours ?? 0), 0);
}

function toLines(byType: Map<string, number>): ResourceRequirementLine[] {
  return [...byType.entries()]
    .filter(([, hours]) => hours > 0.0001)
    .map(([resourceType, hours]) => ({ resourceType, hours }))
    .sort((a, b) => b.hours - a.hours);
}

export function formatHours(hours: number): string {
  const rounded = Math.round(hours * 10) / 10;
  const n = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${n}h`;
}
